import { Box } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Sidebar } from 'widgets/Sidebar';
import { useSidebarStore } from 'widgets/Sidebar/model/state';
import { Snackbar } from 'widgets/Snackbar';
import ImpersonationBanner from 'widgets/ImpersonationBanner/ImpersonationBanner';
import PasswordExpiredModal from 'widgets/PasswordExpiredModal';
import PasswordExpiryBanner from 'widgets/PasswordExpiryBanner';
import { SIDEBAR_LOCKED_ROUTES } from 'constants/sidebar';

export const RootLayout: FunctionComponent = () => {
	const { isSidebarOpened, lockSidebarClosed, unlockSidebar } = useSidebarStore();
	const location = useLocation();

	const isSidebarLockedRoute = useMemo(
		() => SIDEBAR_LOCKED_ROUTES.some((route) => matchPath(route, location.pathname)),
		[location.pathname],
	);

	useEffect(() => {
		if (isSidebarLockedRoute) {
			lockSidebarClosed();
		} else {
			unlockSidebar();
		}
	}, [isSidebarLockedRoute]);

	return (
		<Box
			sx={{
				width: '100vw',
				height: '100vh',
				display: 'flex',
				overflow: 'hidden',
				backgroundColor: ColorPalette.catskillWhite,
			}}
		>
			<Sidebar />
			<Box
				component="main"
				sx={{
					'position': 'relative',
					'padding': '20px',
					'& .notistack-SnackbarContainer': {
						'width': `calc(100% - ${(isSidebarOpened ? 268 : 87) + 80}px)`,
						'right': '40px',
						'& > div': {
							width: '100%',
						},
					},
					'flex': 1,
					'minWidth': 0,
					'minHeight': 0,
					'overflow': 'hidden',
					'display': 'flex',
					'flexDirection': 'column',
					'transition': 'width .3s',
				}}
			>
				<SnackbarProvider
					maxSnack={2}
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					Components={{
						success: Snackbar,
						error: Snackbar,
						warning: Snackbar,
						info: Snackbar,
						default: Snackbar,
					}}
				>
					<ImpersonationBanner />
					<PasswordExpiryBanner />
					<Outlet />
				</SnackbarProvider>
				<PasswordExpiredModal />
			</Box>
		</Box>
	);
};
